
import React, { useEffect, useMemo } from "react"
import { useSelector, useDispatch, shallowEqual } from "react-redux"
import { mapValues, omit, omitBy, pick, pickBy } from "lodash"

function newRefUpdates(refs) {
  const time = (new Date()).getTime()
  return Object.fromEntries( refs.map(ref => [ref, time]) )
}

const isPositive = num => num > 0


export function monitoringState() {
  const initialState = {
    data: {}, 
    state: {}, 
    refs: {},
    recentUpdates: {},
    recentRemovals: {},
    refUpdates: {}
  }

  const actions = {
    addData: (state, {payload: items}) => {
      for (let item in items) {
        state.data[item] = items[item]
        state.state[item] = "ok"
        state.refUpdates[item] = (new Date()).getTime()

        if (!state.refs[item]) {
          state.refs[item] = 0
          state.recentRemovals[item] = 1
        }
      }
    },

    removeData: (state, {payload: items}) => {  
      for (let item of items) {
        delete state.data[item]
        delete state.state[item]
        delete state.refs[item]
        delete state.refUpdates[item]
        delete state.recentUpdates[item]
        delete state.recentRemovals[item]
      }
    },

    incrementRefs: (state, {payload: refs}) => {
      state.refs = incrementKeys(state.refs, refs)
      state.refUpdates =  { ...state.refUpdates, ...newRefUpdates(refs) }
      state.recentUpdates = { ...state.recentUpdates, ...Object.fromEntries(refs.map(ref => [ref, 1]))}
      state.recentRemovals = omit(state.recentRemovals, refs)
    },

    decrementRefs: (state, {payload: refs}) => {
      state.refs = decrementKeys(state.refs, refs)
      state.refUpdates =  { ...state.refUpdates, ...newRefUpdates(refs) }
      state.recentRemovals = { ...state.recentRemovals, ...mapValues(omitBy(state.refs, isPositive), _ => 1) }
    },

    removeRefs: (state, {payload: refs}) => {
      for (let ref of refs) {
        delete state.refs[ref]
        delete state.refUpdates[refs]
      }
    },

    setLoading: (state, {payload: items}) => {
      for (let item of items) {
        state.state[item] = 'loading'
      }
    },

    clearRecentUpdates: (state, {payload: updates}) => {
      state.recentUpdates = omit(state.recentUpdates, updates)
    },

    clearRecentRemovals: (state, {payload: removals}) => {
      state.recentRemovals = omit(state.recentRemovals, removals)
    }
  }

  return { initialState, actions }
}

export const forHeartbeat = ({
  addData: add, 
  removeData: remove, 
  setLoading: load, 
  clearRecentUpdates: clearUpdates,
  clearRecentRemovals: clearRemovals,
  incrementRefs: triggerUpdate
}) => ({ add, remove, load, clearUpdates, clearRemovals, triggerUpdate })

export function useMonitoredState(name, actions, ids) {
  const dispatch = useDispatch()
  const items = useSelector(state => ids.map(id => state[name].data[id]), shallowEqual)
  const states = useSelector(state => ids.map(id => state[name].state[id]), shallowEqual)

  useEffect(() => {
    dispatch(actions.incrementRefs(ids))
    return () => dispatch(actions.decrementRefs(ids))
  }, [ids.join('|')])

  const allLoaded = useMemo(() => states.every(state => state == "ok"), [states])
  
  return { [name]: items, allLoaded }
}

export function incrementKeys(hash, keys) {
  const updatedHash = { ...hash }
  for (let key of keys)
    updatedHash[key] = (updatedHash[key] || 0) + 1

  return updatedHash
}

export function decrementKeys(hash, keys) {
  const updatedHash = { ...hash }
  for (let key of keys)
    updatedHash[key] = (updatedHash[key] || 0) - 1

  return updatedHash
}